import React, { useEffect, useState, useContext } from "react";
import { getItems, deleteItem, updateItem, getItemCategory, getBrand, getItemBatches, adjustItemStock, getUnits } from "../../../Utility/Api";
import { toast } from "react-toastify";
import "../../../Utility/pagination.css";
import ItemForm from "./Form";
import { Deletemodel, Pagination } from "../../Common/Functions";
import { WarehouseContext } from "../../../Utility/UserContext";
import { isReadonlyRoute, NaviGate } from "../../../Utility/function";
import { useLocation } from "react-router-dom";

const Item_list = () => {
  const { pathname } = useLocation();
  const readonly = isReadonlyRoute(pathname);
  
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [deleteModal, showDeleteModal] = useState(false);
  const [deleteID, setDeleteID] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [totalPages, setTotalPages] = useState(0);
  const { selectedWarehouse, shopItem, UserItem, showLoader, hideLoader } = useContext(WarehouseContext);

  const [searchText, setSearchText] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const [batches, setBatches] = useState([]);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustItemProduct, setAdjustItemProduct] = useState(null);
  const [editingBatchId, setEditingBatchId] = useState(null);
  const [newQtyValue, setNewQtyValue] = useState("");
  const [selectedUnitId, setSelectedUnitId] = useState("");
  const [priceUnitValue, setPriceUnitValue] = useState("");
  const [unitsList, setUnitsList] = useState([]);

  useEffect(() => {
    fetchItems();
    fetchCategories();
    fetchBrands();
    fetchUnitsList();
  }, [categoryFilter]);

  const fetchUnitsList = async () => {
    try {
      const response = await getUnits();
      if (response.data && response.data.data) {
        setUnitsList(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching units:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await getItemCategory();
      if (res.data && res.data.data) {
        setCategories(res.data.data.filter(c => c.status == null || c.status == 0));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchBrands = async () => {
    try {
      const res = await getBrand();
      if (res.data && res.data.data) {
        setBrands(res.data.data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchItems = async (page = currentPage, text = searchText) => {
    showLoader();
    try {
      const response = await getItems(page, text, categoryFilter);
      if (response.data && response.data.data) {
        setItems(response.data.data);
        if (response.data.pagination) {
          setTotalPages(response.data.pagination.total_pages);
        } else {
          setTotalPages(0);
        }
      }
      hideLoader();
    } catch (error) {
      console.error("Error fetching items data:", error);
      hideLoader();
    }
  };

  const openAddModal = () => {
    setSelectedItem(null);
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleDeleteModel = (id) => {
    setDeleteID(id);
    showDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      await deleteItem(deleteID);
      showDeleteModal(false);
      toast.error("Item Inactivated successfully");
      fetchItems(currentPage);
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Error deleting item");
    }
  };

  const openAdjustModal = async (item) => {
    setAdjustItemProduct(item);
    setEditingBatchId(null);
    setNewQtyValue("");
    
    // Set default unit based on item's units
    setSelectedUnitId(item?.units || "");
    setPriceUnitValue("0.00");

    showLoader();
    try {
      const response = await getItemBatches(item.id, shopItem?.id);
      if (response.data && response.data.data) {
        // Filter only active batches where stock was added (in_or_out = "0")
        const activeBatches = response.data.data.filter(b => String(b.in_or_out) === "0");
        setBatches(activeBatches);
      } else {
        setBatches([]);
      }
      setShowAdjustModal(true);
    } catch (error) {
      console.error("Error fetching item batches:", error);
      toast.error("Failed to load stock batches");
    } finally {
      hideLoader();
    }
  };

  const handleAdjustSubmit = async (batch) => {
    if (!newQtyValue || isNaN(newQtyValue) || parseFloat(newQtyValue) < 0) {
      toast.error("Please enter a valid quantity");
      return;
    }
    showLoader();
    try {
      const payload = {
        transaction_id: batch.id,
        item_id: adjustItemProduct?.id,
        qty: parseFloat(newQtyValue),
        unit_id: selectedUnitId || "",
        price_unit: parseFloat(priceUnitValue || 0),
        shop_id: shopItem?.id,
        user_id: UserItem?.user_id || "0",
      };
      const response = await adjustItemStock(payload);
      if (response.data && response.data.status === "success") {
        toast.success("Stock adjusted successfully");
        setEditingBatchId(null);
        setNewQtyValue("");
        // Refresh batches list
        const refreshed = await getItemBatches(adjustItemProduct.id, shopItem?.id);
        if (refreshed.data && refreshed.data.data) {
          const activeBatches = refreshed.data.data.filter(b => String(b.in_or_out) === "0");
          setBatches(activeBatches);
        } else {
          setBatches([]);
        }
        // Refresh main items list
        fetchItems(currentPage);
      } else {
        toast.error(response.data.message || "Failed to adjust stock");
      }
    } catch (error) {
      console.error("Error adjusting stock:", error);
      toast.error(error.response?.data?.message || "Failed to adjust stock");
    } finally {
      hideLoader();
    }
  };

  const getUnitName = (unitId) => {
    if (!unitId) return "";
    const found = unitsList.find(u => String(u.id) === String(unitId));
    return found ? found.title : unitId;
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
    fetchItems(pageNumber);
  };

  const handleSearch = (text) => {
    setSearchText(text);
    fetchItems(1, text);
    setCurrentPage(1);
  };

  const activateItem = async (item) => {
    showLoader();
    try {
      const payload = {
        ...item,
        status: "1",
      };
      await updateItem(item.id, payload);
      hideLoader();
      fetchItems(currentPage);
      toast.success("Item Activated successfully");
    } catch (err) {
      console.error(err);
      hideLoader();
      toast.error(err.message || "Failed to activate");
    }
  };

  return (
    <div className="page-wrappers">
      <div className="page-content">
        <NaviGate />
        <div className="mb-0 d-flex justify-content-between align-items-center">
          <h6 className="mb-0 text-uppercase" style={{ fontFamily: "poppins" }}>Item List</h6>
          {!readonly && (
            <button
              type="button"
              onClick={openAddModal}
              className="btn btn-primary mb-0 text-normal rounded-1"
            >
              Add Item
            </button>
          )}
        </div>
        <hr className="mt-1" />

        <div className="card">
          <div className="card-body">
            <div className="row mb-3 align-items-center gap-3">
              <div className="col-12 col-md-4">
                <div className="d-flex position-relative">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search Item title..."
                    value={searchText}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  <img
                    src="/assets/images/icons/search.png"
                    alt="Search Icon"
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      height: "18px",
                      pointerEvents: "none",
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <table
                className="table table-striped table-sm table-bordered"
                style={{ width: "100%", fontFamily: "poppins" }}
              >
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Brand</th>
                    {!readonly && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => {
                    const matchedCat = categories.find(c => String(c.id) === String(item.cat_id));
                    const matchedBrand = brands.find(b => String(b.brand_id) === String(item.brand_id));
                    return (
                      <tr key={item.id}>
                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td>
                          {item.item_name}
                          {item.item_code && <span className="text-muted d-block small" style={{ fontSize: "11px" }}>Code: {item.item_code}</span>}
                        </td>
                        <td>{matchedCat ? matchedCat.categories : "No Category"}</td>
                        <td>{matchedBrand ? matchedBrand.name : "No Brand"}</td>
                         {!readonly && (
                          <td>
                            {item.status == null || item.status == "1" ? (
                              <>
                                <img
                                  src="/assets/images/icons/edit.png"
                                  onClick={() => openEditModal(item)}
                                  height="16px"
                                  alt="Edit"
                                  className="cursor-pointer"
                                  title="Edit"
                                />
                                &nbsp;&nbsp;&nbsp;
                                <img
                                  src="/assets/images/icons/transfer-switch.png"
                                  onClick={() => openAdjustModal(item)}
                                  height="16px"
                                  alt="Adjust Stock"
                                  className="cursor-pointer"
                                  title="Adjust Stock"
                                />
                                &nbsp;&nbsp;&nbsp;
                                <img
                                  src="/assets/images/icons/delete.png"
                                  onClick={() => handleDeleteModel(item.id)}
                                  height="16px"
                                  alt="Delete"
                                  className="cursor-pointer"
                                  title="Delete"
                                />
                              </>
                            ) : (
                              <button
                                className="btn btn-secondary py-0 px-2 rounded-1"
                                style={{ fontSize: "12px" }}
                                onClick={() => activateItem(item)}
                              >
                                Inactive
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {deleteModal && (
        <Deletemodel
          setShowModal={showDeleteModal}
          handleDelete={handleDelete}
        />
      )}

      {showModal && (
        <ItemForm
          selectedProduct={selectedItem}
          closeModal={() => setShowModal(false)}
          refreshList={() => fetchItems(currentPage)}
        />
      )}

      {showAdjustModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border-0 rounded-3 shadow">
              <div className="modal-header px-4 py-3 bg-light d-flex justify-content-between align-items-center">
                <h5 className="modal-title font-bold text-dark" style={{ fontFamily: "poppins" }}>
                  Stock Adjustment: {adjustItemProduct?.item_name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setShowAdjustModal(false)}
                ></button>
              </div>
              <div className="modal-body p-4" style={{ fontFamily: "poppins", maxHeight: "60vh", overflowY: "auto" }}>
                <div className="alert alert-warning text-xs py-2 px-3 rounded-lg mb-3">
                  <strong>Note:</strong> You can adjust any active batch that has remaining stock. Fully consumed batches (remaining = 0.00) are locked to preserve sales records and general ledger integrity.
                </div>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered align-middle text-xs text-normal">
                    <thead className="table-light">
                      <tr>
                        <th>Batch No</th>
                        <th>MFG Date</th>
                        <th>EXP Date</th>
                        <th>Price Unit</th>
                        <th>Original Qty</th>
                        <th>Remaining Qty</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batches.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            <div className="d-flex flex-column align-items-center gap-3">
                              <span className="text-secondary font-semibold">No active stock batches found. Add Initial Stock:</span>
                              <div className="d-flex flex-wrap align-items-center justify-content-center gap-3 mt-2 bg-light p-3 rounded border w-100" style={{ maxWidth: "500px" }}>
                                <div className="d-flex flex-column align-items-start gap-1">
                                  <label className="text-xs font-semibold text-secondary">Quantity</label>
                                  <input
                                    type="number"
                                    step="any"
                                    min="0"
                                    className="form-control form-control-sm text-xs"
                                    style={{ width: "110px" }}
                                    value={newQtyValue}
                                    onChange={(e) => setNewQtyValue(e.target.value)}
                                    onWheel={(e) => e.currentTarget.blur()}
                                    placeholder="Qty"
                                  />
                                </div>
                                <div className="d-flex flex-column align-items-start gap-1">
                                  <label className="text-xs font-semibold text-secondary">Unit</label>
                                  <select
                                    className="form-select form-select-sm text-xs"
                                    style={{ width: "110px" }}
                                    value={selectedUnitId}
                                    onChange={(e) => setSelectedUnitId(e.target.value)}
                                  >
                                    <option value="">Select Unit</option>
                                    {unitsList.filter(u => {
                                      const title = (u.title || "").toLowerCase().trim();
                                      return title === "li" || title === "ml" || title === "litre" || title === "millilitre";
                                    }).map((unit) => (
                                      <option key={unit.id} value={unit.id}>
                                        {unit.title}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="d-flex flex-column align-items-start gap-1">
                                  <label className="text-xs font-semibold text-secondary">Unit Price</label>
                                  <input
                                    type="number"
                                    step="any"
                                    min="0"
                                    className="form-control form-control-sm text-xs"
                                    style={{ width: "110px" }}
                                    value={priceUnitValue}
                                    onChange={(e) => setPriceUnitValue(e.target.value)}
                                    onWheel={(e) => e.currentTarget.blur()}
                                    placeholder="Unit Price"
                                  />
                                </div>
                                <div className="d-flex align-items-end mt-4">
                                  <button
                                    className="btn btn-sm btn-success px-3"
                                    onClick={() => handleAdjustSubmit({ id: null })}
                                  >
                                    Add Stock
                                  </button>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        batches.map((batch) => {
                          const isFullyConsumed = parseFloat(batch.remaining || 0) <= 0;
                          const isEditing = editingBatchId === batch.id;
                          return (
                            <tr key={batch.id} className={isFullyConsumed ? "table-light" : ""}>
                              <td>{batch.unique_id || batch.batch_no || "-"}</td>
                              <td>{batch.manufacturing_date || "-"}</td>
                              <td>{batch.expiry_date || "-"}</td>
                              <td>{parseFloat(batch.purchase_price || 0).toFixed(2)}</td>
                              <td>{parseFloat(batch.qty || 0).toFixed(2)}</td>
                              <td className="font-semibold">{parseFloat(batch.remaining || 0).toFixed(2)}</td>
                              <td>
                                {isEditing ? (
                                  <div className="d-flex align-items-center gap-2">
                                    <input
                                      type="number"
                                      step="any"
                                      min="0"
                                      className="form-control form-control-sm text-xs py-0.5 px-1.5"
                                      style={{ width: "80px" }}
                                      value={newQtyValue}
                                      onChange={(e) => setNewQtyValue(e.target.value)}
                                      onWheel={(e) => e.currentTarget.blur()}
                                      placeholder="Qty"
                                      autoFocus
                                    />
                                    <button
                                      className="btn btn-xs btn-success py-0.5 px-2"
                                      onClick={() => handleAdjustSubmit(batch)}
                                    >
                                      Save
                                    </button>
                                    <button
                                      className="btn btn-xs btn-light py-0.5 px-1.5 border"
                                      onClick={() => setEditingBatchId(null)}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    className="btn btn-xs btn-outline-primary py-1 px-2 rounded font-semibold"
                                    onClick={() => {
                                      setEditingBatchId(batch.id);
                                      const val = parseFloat(batch.remaining || 0);
                                      const formattedVal = (val > 0 && val < 0.01)
                                        ? parseFloat(val.toFixed(4)).toString()
                                        : parseFloat(val.toFixed(2)).toString();
                                      setNewQtyValue(formattedVal);
                                    }}
                                  >
                                    Adjust Qty
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer border-t px-4 py-2 bg-light text-end">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowAdjustModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Item_list;
